import React, { useEffect, useState } from 'react';
import { Typography, Breadcrumb, Card, Form, Input, Button, Space, message, List, Popconfirm, Select, Modal, Tabs, Collapse, Row, Col } from 'antd';
import axios from 'axios';
import { PlusOutlined, ApiOutlined, EditOutlined } from '@ant-design/icons';

const { Title, Paragraph } = Typography;


const getErrorMessage = (error, fallback = 'An error occurred') => {
  const apiError = error?.response?.data?.error;
  if (typeof apiError === 'string') return apiError;
  if (apiError && typeof apiError === 'object') return apiError.message || JSON.stringify(apiError);
  return error?.message || fallback;
};

const OtherPlatforms = () => {
  // GeoTab state
  const [geotabAccounts, setGeotabAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [creatingAcc, setCreatingAcc] = useState(false);
  const [authenticating, setAuthenticating] = useState(false);
  const [calling, setCalling] = useState(false);
  const [newAccForm] = Form.useForm();
  const [rpcForm] = Form.useForm();
  const [sessions, setSessions] = useState({}); // { [accountId]: sessionId }
  
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [previewFilename, setPreviewFilename] = useState('response.json');
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [renameForm] = Form.useForm();
  const [renaming, setRenaming] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);

  useEffect(() => {
    // Load GeoTab accounts
    (async () => {
      try {
        setLoadingAccounts(true);
        const { data } = await axios.get('/api/integrations/geotab/accounts');
        setGeotabAccounts(data || []);
      } catch (e) {
        message.error(getErrorMessage(e, 'Failed to load GeoTab accounts'));
      } finally {
        setLoadingAccounts(false);
      }
    })();
  }, []);

  // GeoTab: create account
  const createAccount = async () => {
    try {
      const values = await newAccForm.validateFields();
      setCreatingAcc(true);
      const headers = {};
      const token = sessionStorage.getItem('token');
      if (token) headers.Authorization = `Bearer ${token}`;
      await axios.post('/api/integrations/geotab/accounts', values, { headers });
      newAccForm.resetFields();
      const { data } = await axios.get('/api/integrations/geotab/accounts');
      setGeotabAccounts(data || []);
      message.success('GeoTab account added');
    } catch (e) {
      if (e?.errorFields) return;
      message.error(getErrorMessage(e, 'Failed to add account'));
    } finally {
      setCreatingAcc(false);
    }
  };

  // Download last JSON result shown in the Result textarea
  const downloadJson = () => {
    try {
      const vals = rpcForm.getFieldsValue(['typeName', 'method', 'result_preview']);
      const content = previewContent || vals.result_preview;
      if (!content) {
        message.warning('No result to download');
        return;
      }
      const stamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\..+/, '').replace('T', '_');
      const baseName = (vals.typeName && vals.typeName.trim()) || (vals.method && vals.method.trim()) || 'response';
      const filename = previewFilename || `${baseName}_${stamp}.json`;
      const blob = new Blob([content], { type: 'application/json;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      message.error('Failed to download JSON');
    }
  };

  // Open preview modal for current JSON result
  const previewJson = () => {
    const vals = rpcForm.getFieldsValue(['typeName', 'method', 'result_preview']);
    if (!vals.result_preview) {
      message.warning('No result to preview');
      return;
    }
    const stamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\..+/, '').replace('T', '_');
    const baseName = (vals.typeName && vals.typeName.trim()) || (vals.method && vals.method.trim()) || 'response';
    setPreviewFilename(`${baseName}_${stamp}.json`);
    setPreviewContent(vals.result_preview);
    setPreviewOpen(true);
  };

  const refreshAccounts = async () => {
    try {
      setLoadingAccounts(true);
      const { data } = await axios.get('/api/integrations/geotab/accounts');
      setGeotabAccounts(data || []);
    } catch (e) {
      message.error(e.response?.data?.error || 'Failed to load GeoTab accounts');
    } finally {
      setLoadingAccounts(false);
    }
  };

  const deleteAccount = async (id) => {
    try {
      const headers = {};
      const token = sessionStorage.getItem('token');
      if (token) headers.Authorization = `Bearer ${token}`;
      await axios.delete(`/api/integrations/geotab/accounts/${id}`, { headers });
      setGeotabAccounts((arr) => arr.filter(a => a.id !== id));
      message.success('Deleted');
    } catch (e) {
      message.error(getErrorMessage(e, 'Failed to delete account'));
    }
  };

  const openRenameModal = (account) => {
    setSelectedAccount(account);
    renameForm.setFieldsValue({ name: account.name || account.database });
    setRenameModalOpen(true);
  };

  const handleRenameAccount = async () => {
    try {
      if (!selectedAccount) return;
      const { name } = await renameForm.validateFields();
      setRenaming(true);
      const headers = {};
      const token = sessionStorage.getItem('token');
      if (token) headers.Authorization = `Bearer ${token}`;
      await axios.put(`/api/integrations/geotab/accounts/${selectedAccount.id}`, { name }, { headers });
      message.success('Account renamed');
      setRenameModalOpen(false);
      renameForm.resetFields();
      setSelectedAccount(null);
      await refreshAccounts();
    } catch (e) {
      if (e?.errorFields) return;
      message.error(getErrorMessage(e, 'Failed to rename account'));
    } finally {
      setRenaming(false);
    }
  };

  // GeoTab: authenticate selected account
  const authenticate = async () => {
    try {
      const { accountId } = await rpcForm.validateFields(['accountId']);
      const acc = geotabAccounts.find(a => a.id === accountId);
      if (!acc) return message.error('Select an account');
      setAuthenticating(true);
      const { data } = await axios.post('/api/integrations/geotab/authenticate', { accountId: acc.id });
      const sess = data?.credentials?.sessionId || data?.sessionId;
      if (!sess) throw new Error('No sessionId in response');
      setSessions((s) => ({ ...s, [accountId]: sess }));
      // Show JSON result and populate Session ID field for convenience
      rpcForm.setFieldsValue({ result_preview: JSON.stringify(data, null, 2), sessionId: sess, method: 'Authenticate' });
      message.success('Authenticated');
    } catch (e) {
      message.error(getErrorMessage(e, 'Authentication failed'));
    } finally {
      setAuthenticating(false);
    }
  };

  // GeoTab: call method
  const callMethod = async () => {
    try {
      const vals = await rpcForm.validateFields();
      if (vals.method === 'Authenticate') {
        // Route Authenticate calls to the dedicated flow to ensure proper credential usage
        await authenticate();
        return;
      }
      const acc = geotabAccounts.find(a => a.id === vals.accountId);
      if (!acc) return message.error('Select an account');
      setCalling(true);
      // build params
      let params = {};
      if (vals.method === 'Get' && vals.typeName) params.typeName = vals.typeName;
      if (vals.params_json) {
        try { params = { ...params, ...JSON.parse(vals.params_json) }; } catch { throw new Error('Invalid JSON in Params'); }
      }
      const sessionId = vals.sessionId || sessions[vals.accountId];
      const headers = {};
      const token = sessionStorage.getItem('token');
      if (token) headers.Authorization = `Bearer ${token}`;
      const payload = {
        accountId: acc.id,
        method: vals.method,
        params: sessionId ? { ...params, credentials: { sessionId } } : params,
        id: 1,
      };
      const { data } = await axios.post('/api/integrations/geotab/call', payload, { headers });
      message.success('Call success');
      const content = JSON.stringify(data, null, 2);
      rpcForm.setFieldsValue({ result_preview: content });
      // Auto-open preview modal for convenience
      const stamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\..+/, '').replace('T', '_');
      const baseName = (vals.typeName && vals.typeName.trim()) || (vals.method && vals.method.trim()) || 'response';
      setPreviewFilename(`${baseName}_${stamp}.json`);
      setPreviewContent(content);
      setPreviewOpen(true);
    } catch (e) {
      if (e?.errorFields) return;
      message.error(getErrorMessage(e, 'Call failed'));
    } finally {
      setCalling(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[
        { title: 'Admin' },
        { title: 'Integrations' },
        { title: 'GeoTab' },
      ]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>GeoTab Integration</Title>
      </div>

      <Card title="GeoTab Integration" loading={loadingAccounts}>
        <Paragraph type="secondary">
          Manage multiple GeoTab databases and perform JSON-RPC calls.
        </Paragraph>

        <Tabs defaultActiveKey="accounts" items={[
          {
            key: 'accounts',
            label: 'Accounts',
            children: (
              <div>
                <List
                  dataSource={geotabAccounts}
                  locale={{ emptyText: 'No accounts configured' }}
                  renderItem={(item) => (
                    <List.Item
                      style={{ background: '#fafafa', marginBottom: 8, borderRadius: 8, padding: 12 }}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            <b style={{ fontSize: 16 }}>{item.name || item.database}</b>
                            <div style={{ color: '#666', fontSize: 12, marginTop: 4 }}>
                              {item.base_url} • {item.username} • DB: {item.database}
                            </div>
                            {sessions[item.id] && (
                              <div style={{ color: '#52c41a', fontSize: 11, marginTop: 2 }}>✓ Authenticated</div>
                            )}
                          </div>
                          <Space>
                            {sessions[item.id] && (
                              <Button size="small" onClick={async () => {
                                try {
                                  await navigator.clipboard.writeText(sessions[item.id]);
                                  message.success('Session copied');
                                } catch {
                                  message.error('Copy failed');
                                }
                              }}>Copy Session</Button>
                            )}
                            <Button size="small" icon={<EditOutlined />} onClick={() => openRenameModal(item)}>Rename</Button>
                            <Popconfirm title="Delete this account?" onConfirm={() => deleteAccount(item.id)}>
                              <Button danger size="small">Delete</Button>
                            </Popconfirm>
                          </Space>
                        </div>
                      </div>
                    </List.Item>
                  )}
                  style={{ marginBottom: 16 }}
                />

                <Collapse
                  items={[{
                    key: '1',
                    label: <span><PlusOutlined /> Add New Account</span>,
                    children: (
                      <Form layout="vertical" form={newAccForm}>
                        <Row gutter={16}>
                          <Col span={12}>
                            <Form.Item label="Account Name" name="name">
                              <Input placeholder="e.g., Production DB" />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item label="Database" name="database" rules={[{ required: true }]}>
                              <Input placeholder="Database name" />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Row gutter={16}>
                          <Col span={12}>
                            <Form.Item label="Username" name="username" rules={[{ required: true }]}>
                              <Input placeholder="GeoTab username" />
                            </Form.Item>
                          </Col>
                          <Col span={12}>
                            <Form.Item label="Password" name="password">
                              <Input.Password placeholder="Password (optional if using session)" />
                            </Form.Item>
                          </Col>
                        </Row>
                        <Form.Item label="Base URL" name="base_url" initialValue="https://my.geotab.com">
                          <Input />
                        </Form.Item>
                        <Form.Item label="Session ID (optional)" name="session_id">
                          <Input placeholder="Auto-filled or paste manually" />
                        </Form.Item>
                        <Space>
                          <Button
                            onClick={async () => {
                              try {
                                const vals = await newAccForm.validateFields(['base_url','database','username','password']);
                                if (!vals.password) return message.error('Enter password to fetch session');
                                const { data } = await axios.post('/api/integrations/geotab/authenticate', {
                                  base_url: vals.base_url,
                                  userName: vals.username,
                                  password: vals.password,
                                  database: vals.database,
                                });
                                const sess = data?.credentials?.sessionId;
                                if (!sess) return message.error('No sessionId returned');
                                newAccForm.setFieldsValue({ session_id: sess });
                                message.success('Session ID fetched');
                              } catch (e) {
                                if (e?.errorFields) return;
                                message.error(getErrorMessage(e, 'Failed to get session'));
                              }
                            }}
                          >Get Session</Button>
                          <Button type="primary" onClick={createAccount} loading={creatingAcc} icon={<PlusOutlined />}>Add Account</Button>
                        </Space>
                      </Form>
                    )
                  }]}
                />
              </div>
            )
          },
          {
            key: 'api',
            label: <span><ApiOutlined /> API Testing</span>,
            children: (
              <div>

                <Form layout="vertical" form={rpcForm}>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item label="Account" name="accountId" rules={[{ required: true }]}> 
                        <Select
                          placeholder="Select account"
                          options={geotabAccounts.map(a => ({ value: a.id, label: `${a.name || a.database}` }))}
                          showSearch
                          optionFilterProp="label"
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Method" name="method" rules={[{ required: true }]} initialValue="Get">
                        <Select
                          options={[
                            { value: 'Authenticate', label: 'Authenticate' },
                            { value: 'Get', label: 'Get' },
                            { value: 'GetCountOf', label: 'GetCountOf' },
                            { value: 'Add', label: 'Add' },
                            { value: 'Set', label: 'Set' },
                            { value: 'Remove', label: 'Remove' },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item label="Type Name (for Get/GetCountOf)" name="typeName">
                        <Select
                          placeholder="e.g., Device, Trip, User"
                          showSearch
                          allowClear
                          options={[
                            { value: 'Device', label: 'Device' },
                            { value: 'Trip', label: 'Trip' },
                            { value: 'User', label: 'User' },
                            { value: 'Driver', label: 'Driver' },
                            { value: 'Zone', label: 'Zone' },
                            { value: 'Route', label: 'Route' },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Session ID (optional)" name="sessionId">
                        <Input placeholder="Auto-filled after authenticate" />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item label="Additional Params (JSON)" name="params_json">
                    <Input.TextArea rows={3} placeholder='Optional, e.g. { "search": { "name": "*" } }' />
                  </Form.Item>
                  
                  <div style={{ background: '#f0f2f5', padding: 12, borderRadius: 8, marginBottom: 16 }}>
                    <div style={{ fontWeight: 600, marginBottom: 10, color: '#1890ff', fontSize: 13 }}>Follow These Steps:</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                      <Button 
                        size="small"
                        onClick={authenticate} 
                        loading={authenticating}
                        style={{ background: 'white', height: 'auto', padding: '6px 10px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <div style={{ fontSize: 14, fontWeight: 'bold' }}>1</div>
                        <div style={{ fontSize: 12 }}>Authenticate</div>
                      </Button>
                      <Button 
                        size="small"
                        onClick={callMethod} 
                        loading={calling}
                        style={{ background: 'white', height: 'auto', padding: '6px 10px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <div style={{ fontSize: 14, fontWeight: 'bold' }}>2</div>
                        <div style={{ fontSize: 12 }}>Execute Call</div>
                      </Button>
                      <Button 
                        size="small"
                        onClick={previewJson}
                        style={{ background: 'white', height: 'auto', padding: '6px 10px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}
                      >
                        <div style={{ fontSize: 14, fontWeight: 'bold' }}>3</div>
                        <div style={{ fontSize: 12 }}>View Result</div>
                      </Button>
                    </div>
                  </div>
                  
                  <Form.Item label="Response" name="result_preview">
                    <Input.TextArea rows={8} readOnly style={{ fontFamily: 'monospace', fontSize: 12, background: '#fafafa' }} />
                  </Form.Item>
                </Form>
              </div>
            )
          }
        ]} />
      </Card>
      <Modal
        title="JSON Preview"
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={[
          <Button key="copy" onClick={async () => {
            try {
              await navigator.clipboard.writeText(previewContent);
              message.success('Copied');
            } catch {
              message.error('Copy failed');
            }
          }}>Copy</Button>,
          <Button key="download" type="primary" onClick={downloadJson}>Download</Button>
        ]}
        width={800}
      >
        <div style={{ maxHeight: 500, overflow: 'auto', background: '#0f172a', padding: 12, borderRadius: 6 }}>
          <pre style={{ color: '#e2e8f0', margin: 0 }}>{previewContent}</pre>
        </div>
        <div style={{ marginTop: 8, color: '#888' }}>{previewFilename}</div>
      </Modal>
      <Modal
        title="Rename GeoTab Account"
        open={renameModalOpen}
        onCancel={() => {
          setRenameModalOpen(false);
          renameForm.resetFields();
          setSelectedAccount(null);
        }}
        onOk={handleRenameAccount}
        okButtonProps={{ loading: renaming }}
        destroyOnClose
      >
        <Form form={renameForm} layout="vertical">
          <Form.Item
            label="Account Name"
            name="name"
            rules={[{ required: true, message: 'Please enter a name' }]}
          >
            <Input placeholder="Enter new account name" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default OtherPlatforms;
